import { HttpClient, Logger, LogLevel } from '@openticket/lib-shop';
import urlJoin from 'url-join';
import { useEnv } from '../shopenv';

export interface BotProtConfig {
    httpClient?: HttpClient;
    logger: Logger;
    shopApiUrl: string;
    deferredShopApiUrl?: string;
    serviceUrl: string;
    errorHtmlTemplate?: string;
    errorCtaHtmlTemplate?: string;
    loaderHtmlTemplate?: string;
}

interface BotProtEventDetailFinal {
    // Final step retrieval & all other steps hand-in OK
    final: true;
    jwt: string;
}

interface BotProtEventDetailHandIn {
    // All non-final step hand-in responses
    final: false;
    jwt: string;
    next: string;
    status: number;
}

interface BotProtEventDetailError {
    // Transport errors
    status: number;
}

interface ErrorRenderData {
    message?: string;
    title?: string;
    cta?: string;
}

const BOTPROT_EVENT_LISTENER = 'BotProt:StepHandled';
// Todo: remove me (and my listener management) when botprot is released without this event
const BOTPROT_EVENT_LISTENER_DEPRECATED = 'BotProt:ChallengeHandled';
const BOTPROT_ERROR_CTA_EVENT_LISTENER = 'BotProt:Error:CTA';

const defaultLoaderHtml = '<div class="loader"><h2>...</h2></div>';
const defaultErrorCtaHtml = `<button onclick="document.dispatchEvent(new CustomEvent('${BOTPROT_ERROR_CTA_EVENT_LISTENER}'))" class="ot-button is-fullwidth" style="gap: .5rem;">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="oti" style="width: 1.5rem; height: 1.5rem;"><path transform="scale (-1, 1)" transform-origin="center" fill-rule="evenodd" clip-rule="evenodd" d="M8.221 10.885L12.3214 9.46501L13.2454 12.1331L6.22662 14.5637L4.43061 13.6916L2 6.67286L4.66808 5.7489L5.9401 9.42209C7.17299 7.32015 8.97941 5.59523 11.1649 4.46107C13.9154 3.03372 17.0853 2.63593 20.1031 3.3394C23.121 4.04286 25.7883 5.80131 27.6241 8.29772C29.4599 10.7941 30.3435 13.8643 30.1156 16.9546C29.8877 20.045 28.5632 22.9523 26.381 25.1523C24.1988 27.3524 21.3024 28.7005 18.214 28.9536C15.1256 29.2067 12.0483 28.3482 9.53706 26.5328C7.02576 24.7174 5.24564 22.0645 4.5176 19.0525L7.14883 18.4165C7.72525 20.8012 9.13466 22.9016 11.123 24.339C13.1113 25.7763 15.5476 26.4561 17.9929 26.2557C20.4381 26.0553 22.7313 24.9879 24.4591 23.246C26.1868 21.5041 27.2354 19.2023 27.4159 16.7555C27.5964 14.3087 26.8968 11.878 25.4433 9.90145C23.9898 7.92493 21.878 6.53269 19.4886 5.97572C17.0992 5.41876 14.5895 5.73371 12.4118 6.8638C10.6492 7.7785 9.19803 9.17852 8.221 10.885Z" fill="currentColor"></path></svg>
    <span>{ctaText}</span>
</button>`;
const defaultErrorHtml = `<div style="padding: 1rem;">
    <h2>{title}</h2>
    <p>{message}</p>
    {cta}
</div>`;

export class BotProtClient {
    log: Logger;
    serviceUrl: string;
    shopApiUrl: string;
    deferredShopApiUrl?: string;
    data?: Record<string, unknown>;
    identifier?: string;
    httpClient?: HttpClient;

    errorHtmlTemplate: string;
    errorCtaHtmlTemplate: string;
    loaderHtmlTemplate: string;

    onFinal: (jwt?: string) => void;

    private elementToRenderIn?: HTMLElement;

    /**
     * Creates a new BotProtClient instance.
     * Set onFinal to be called when the step(s) are completed.
     * @param config The configuration for the bot prot client.
     */
    constructor(config: BotProtConfig) {
        this.httpClient = config.httpClient;
        this.log = config.logger;
        this.shopApiUrl = config.shopApiUrl;
        this.deferredShopApiUrl = config.deferredShopApiUrl;
        this.serviceUrl = config.serviceUrl;

        this.errorHtmlTemplate = config.errorHtmlTemplate || defaultErrorHtml;
        this.errorCtaHtmlTemplate =
            config.errorCtaHtmlTemplate || defaultErrorCtaHtml;
        this.loaderHtmlTemplate =
            config.loaderHtmlTemplate || defaultLoaderHtml;

        this.onFinal = () => {
            /* No-op */
        };
    }

    /**
     * Initializes the bot protection, returns false if protection for this shop is not enabled.
     * @param identifier An identifier to distinguish requests from each other.
     * @param data Could be any object to send to the botprot service
     */
    async init(
        identifier: string,
        data?: Record<string, unknown>
    ): Promise<boolean> {
        if (!identifier) {
            throw new Error('BotProt: identifier is required');
        }

        this.data = data;
        this.identifier = identifier;

        return isBotProtEnabled(this.log, identifier, data);
    }

    /**
     * Renders the bot protection step in the given element.
     * @param elementToRenderIn The element to render the step in.
     * @param loaderHtml Optional HTML to render while the step is being fetched.
     * @param resumeSessionUrl Optional to resume a previous session. Sessions cannot be resumed twice.
     */
    async render(
        elementToRenderIn: HTMLElement,
        loaderHtml?: string,
        resumeSessionUrl?: string
    ): Promise<void> {
        document.addEventListener(BOTPROT_EVENT_LISTENER, this.handleStep);
        document.addEventListener(
            BOTPROT_EVENT_LISTENER_DEPRECATED,
            this.handleStep
        );
        document.addEventListener(
            BOTPROT_ERROR_CTA_EVENT_LISTENER,
            this.handleErrorCta,
            { once: true }
        );

        if (loaderHtml) {
            this.loaderHtmlTemplate = loaderHtml;
        }

        this.elementToRenderIn = elementToRenderIn;
        this.elementToRenderIn.innerHTML = this.loaderHtmlTemplate;

        const stepLocation = resumeSessionUrl || (await this.initBotprot());
        if (!stepLocation) {
            this.renderError();
            return;
        }

        try {
            const html = await this.getStep(stepLocation);

            await this.renderStep(html);
        } catch (error) {
            this.renderError(error);
        }
    }

    // Arrow function for preserving 'this' context
    handleErrorCta = async (): Promise<void> => {
        document.removeEventListener(BOTPROT_EVENT_LISTENER, this.handleStep);
        document.removeEventListener(
            BOTPROT_EVENT_LISTENER_DEPRECATED,
            this.handleStep
        );
        document.removeEventListener(
            BOTPROT_ERROR_CTA_EVENT_LISTENER,
            this.handleErrorCta
        );

        if (!this.elementToRenderIn) {
            // This error will never be recoverable. In the future a better
            // error callback can be used to propagate the error to the consumer.
            throw new Error(
                'Element to render in is not defined. Failed to restart'
            );
        }

        await this.render(this.elementToRenderIn);
    };

    /**
     * Renders the given HTML inside a div with the step html. Scripts are
     * recreated and replaced.
     * @param html
     * @private
     */
    private async renderStep(html?: string): Promise<void> {
        if (!this.elementToRenderIn || !html) {
            this.log.create(
                LogLevel.Warning,
                ['botprot', 'render', 'failed'],
                {
                    identifier: this.identifier,
                    htmlSet: !!html,
                },
                'BotProt render failed. elementToRenderIn or html not set.'
            );

            return;
        }

        const container = document.createElement('div');
        container.innerHTML = html;

        // Replace scripts with new (running) ones
        const scripts = container.querySelectorAll('script');
        scripts.forEach(oldScript => {
            const newScript = document.createElement('script');

            Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
            });
            newScript.appendChild(document.createTextNode(oldScript.innerHTML));
            if (oldScript.parentNode) {
                oldScript.parentNode.replaceChild(newScript, oldScript);
            }
        });

        this.elementToRenderIn.replaceChildren(container);
    }

    /**
     * Initializes the step at the BotProt service. Returns the stepId that needs to load
     */
    private async initBotprot(): Promise<string> {
        const url = this.buildInitBotprotUrl(
            urlJoin(this.serviceUrl, 'init', this.identifier || ''),
            this.data
        );

        let response: Response | undefined;

        try {
            const returnUrl = new URL(window.location.href);
            // Ensure we don't recursively overinflate the JWT!!!
            returnUrl.searchParams.delete('botprot_next');

            response = await this.fetchWithRetries(url, 5, 10000, {
                method: 'POST',
                body: JSON.stringify({
                    // URL query state (e.g. eventId/event) self-heals on reload.
                    // Non-URL shop state (filters, etc.) is lost after the redirect.
                    return_url: returnUrl.href,
                }),
            });
            return getLocationFromResponse(response);
        } catch (e) {
            this.log.create(
                LogLevel.Error,
                ['botprot', 'init', 'call', 'failed', 'response'],
                {
                    error: e,
                    url,
                    identifier: this.identifier,
                    status: response?.status,
                    statusText: response?.statusText,
                    ok: response?.ok,
                    headers: response?.headers,
                },
                'BotProt init call failed. Invalid response.',
                e
            );

            // Not re-throwing the error here as there is no error data/reason
            // that should reach the user. An empty location is enough.
        }

        return '';
    }

    // Arrow function for preserving 'this' context
    handleStep = async (event: Event): Promise<void> => {
        // Operation order is important.
        // Operation 1: A final event should always take precedent.
        if (isBotProtEventDetailFinal(event)) {
            this.finalizeAndCleanup(event.detail.jwt);

            return;
        }

        // Operation 2: An error event should be handled before a 'hand-in' event.
        if (isBotProtEventDetailError(event)) {
            this.log.create(
                LogLevel.Warning,
                ['botprot', 'handleStep', 'error'],
                {
                    event: event.detail,
                    identifier: this.identifier,
                },
                'BotProt handleStep called with an error event.'
            );

            // TODO This should be moved to user interaction (show error + button)
            // 1. Services should have as little untraceable side-effects.
            // 2. A recurring error should not flood the system.
            this.renderError(`Error status: ${event.detail.status}`);

            return;
        }

        // Operation 3: A 'hand-in' event should be handled last.
        // If this guard is false, the botprot service dispatched an invalid event.
        if (isBotProtEventDetailHandIn(event)) {
            try {
                const html = await this.getStep(event.detail.next);

                await this.renderStep(html);
            } catch (e) {
                this.renderError(e);
            }

            return;
        }

        // Operation 4: If no next step is provided, log a warning and refresh.
        // This is an implementation error in the botprot service.
        this.log.create(
            LogLevel.Warning,
            ['botprot', 'handleStep', 'event', 'invalid'],
            { event, identifier: this.identifier },
            'BotProt handleStep called with invalid event.'
        );

        // TODO This should be moved to user interaction (show error + button)
        // 1. Services should have as little untraceable side-effects.
        // 2. A recurring error should not flood the system.
        this.renderError(`System error: invalid event`);
    };

    private finalizeAndCleanup(jwt: string): void {
        if (!jwt) {
            this.log.create(
                LogLevel.Error,
                ['botprot', 'finalize', 'missing', 'jwt'],
                { identifier: this.identifier },
                'BotProt finalizeAndCleanup called with no JWT.'
            );

            return;
        }

        // TODO: This should be somewhere else if we want this to be a library
        if (this.httpClient) {
            this.httpClient.interceptors.request.use(request => {
                const apiHost = new URL(this.shopApiUrl).hostname;
                const deferredApiHost = this.deferredShopApiUrl
                    ? new URL(this.deferredShopApiUrl).hostname
                    : '';
                const interceptedHost = new URL(request.url || '').hostname;

                if (!jwt || !interceptedHost) {
                    return request;
                }

                if ([apiHost, deferredApiHost].includes(interceptedHost)) {
                    // Only add the header for shop-api operations.
                    request.headers.set('x-botprot-verification', jwt);
                }

                return request;
            });
        }

        document.removeEventListener(BOTPROT_EVENT_LISTENER, this.handleStep);

        document.removeEventListener(
            BOTPROT_EVENT_LISTENER_DEPRECATED,
            this.handleStep
        );

        document.removeEventListener(
            BOTPROT_ERROR_CTA_EVENT_LISTENER,
            this.handleErrorCta
        );

        this.onFinal(jwt);
    }

    /**
     * Fetches the supplied step from the BotProt service. Returns the HTML to render the step.
     * @param url The url returned by the BotProt service when registering the step.
     * @returns The HTML to render the step.
     */
    private async getStep(url: string): Promise<string> {
        let response: Response | undefined;

        try {
            response = await this.fetchWithRetries(url);
            // Normally response.ok would be checked here;
            // however, that is already done in fetchWithRetries.

            const html = (await response.text()).trim();

            if (!html) {
                // Only logging is needed here, throwing in an event
                // handler is useless. Might as well let the renderer handle it.
                this.log.create(
                    LogLevel.Warning,
                    ['botprot', 'step', 'loading', 'empty'],
                    {
                        identifier: this.identifier,
                        url,
                        status: response?.status,
                        statusText: response?.statusText,
                        ok: response?.ok,
                    },
                    `Failed to fetch step. Body is empty`
                );
            }

            return html;
        } catch (error) {
            // Only logging is needed here, throwing in an event
            // handler is useless. Might as well let the renderer handle it.
            this.log.create(
                LogLevel.Error,
                ['botprot', 'step', 'loading', 'failed'],
                {
                    identifier: this.identifier,
                    url,
                    status: response?.status,
                    statusText: response?.statusText,
                    ok: response?.ok,
                },
                `Failed to fetch step`,
                error
            );

            throw error;
        }
    }

    private async fetchWithRetries(
        url: string,
        maxRetries = 5,
        delayMs = 10000,
        requestInit?: RequestInit
    ) {
        const fetchFn = async () => {
            const response = await fetch(url, requestInit);

            switch (response.status) {
                case 429:
                    throw new Error(`BotProt:Error - 429`);
                default:
                    // continue
                    break;
            }

            // Fetch only rejects on network errors.
            // We throw manually if the status isn't 2xx so it triggers a retry.
            if (!response.ok) {
                throw new Error(`HTTP error status: ${response.status}`);
            }

            // If successful, return the response immediately
            return response;
        };

        for (let attempt = 1; ; attempt++) {
            try {
                return await fetchFn();
            } catch (e) {
                if (e.message.startsWith('BotProt:Error')) {
                    throw e;
                }

                if (attempt > maxRetries) {
                    throw new Error(
                        `Failed to fetch after ${maxRetries} attempts. Last error: ${e.message}`
                    );
                }

                await new Promise(resolve => setTimeout(resolve, delayMs));
            }
        }
    }

    private buildInitBotprotUrl(
        url: string,
        data?: Record<string, unknown>
    ): string {
        const u = new URL(url);

        if (data) {
            Object.entries(data).forEach(([key, value]) => {
                if (value === null || value === undefined) {
                    return;
                } else if (typeof value === 'object') {
                    u.searchParams.append(
                        key.toLowerCase(),
                        JSON.stringify(value)
                    );
                } else {
                    try {
                        u.searchParams.append(
                            key.toLowerCase(),
                            value.toString()
                        );
                    } catch (e) {
                        /* No-op */
                    }
                }
            });
        }

        return u.toString();
    }

    private renderError(error?: unknown) {
        if (!this.elementToRenderIn) {
            return;
        }

        const { message, title, cta }: ErrorRenderData = errorToData(error);

        const ctaHtml = cta
            ? this.errorCtaHtmlTemplate.replace('{ctaText}', () => cta)
            : null;

        this.elementToRenderIn.innerHTML = this.errorHtmlTemplate
            .replace('{title}', () => title || 'Failed to verify connection.')
            .replace('{message}', () => message || '')
            .replace('{cta}', () => ctaHtml || '');
    }
}

async function isBotProtEnabled(
    log: Logger,
    identifier: string,
    data?: Record<string, unknown>
): Promise<boolean> {
    const { botProtS3UrlPrefix, botProtStatus } = useEnv();

    if (botProtStatus === 'disabled') {
        return false;
    }

    if (botProtStatus === 'forced') {
        return true;
    }

    try {
        // Quick check to see if this identifier needs a botprot step
        const response = await fetch(urlJoin(botProtS3UrlPrefix, identifier), {
            method: 'HEAD',
        });

        if (!response.ok) {
            log.create(
                LogLevel.Warning,
                ['botprot', 'init', 'head', 'failed'],
                { ...data, identifier },
                'BotProt init head call failed.'
            );
        }

        return response.ok;
    } catch (e) {
        log.create(
            LogLevel.Warning,
            ['botprot', 'init', 'fetch', 'failed'],
            { identifier, error: e },
            'BotProt init fetch call failed.'
        );

        return false;
    }
}

function isCustomEvent(value: Event): value is CustomEvent {
    return (
        !!value &&
        'detail' in value &&
        !!value.detail &&
        typeof value.detail === 'object'
    );
}

function isBotProtEventDetailFinal(
    value: Event
): value is CustomEvent<BotProtEventDetailFinal> {
    if (!isCustomEvent(value)) {
        return false;
    }

    const { detail } = value;

    if (!('final' in detail) || detail.final !== true) {
        return false;
    }

    return 'jwt' in detail && typeof detail.jwt === 'string' && !!detail.jwt;
}

function isBotProtEventDetailHandIn(
    value: Event
): value is CustomEvent<BotProtEventDetailHandIn> {
    if (!isCustomEvent(value)) {
        return false;
    }

    const { detail } = value;

    if (!('final' in detail) || detail.final !== false) {
        return false;
    }

    if (!('jwt' in detail) || typeof detail.jwt !== 'string' || !detail.jwt) {
        return false;
    }

    if (
        !('next' in detail) ||
        typeof detail.next !== 'string' ||
        !detail.next
    ) {
        return false;
    }

    return (
        'status' in detail &&
        typeof detail.status === 'number' &&
        detail.status >= 200 &&
        detail.status < 400
    );
}

// Matches events whose status falls outside the 2xx-3xx success range
// (i.e. transport/error statuses). Hand-in events carry a 2xx-3xx status and
// are therefore handled by isBotProtEventDetailHandIn instead.
function isBotProtEventDetailError(
    value: Event
): value is CustomEvent<BotProtEventDetailError> {
    if (!isCustomEvent(value)) {
        return false;
    }

    const { detail } = value;

    return (
        'status' in detail &&
        typeof detail.status === 'number' &&
        (detail.status >= 400 || detail.status < 200)
    );
}

function errorToData(error?: unknown): ErrorRenderData {
    if (!error) {
        return {};
    }

    if (typeof error === 'string') {
        return { message: error };
    }

    if (
        typeof error !== 'object' ||
        !('message' in error) ||
        typeof error.message !== 'string'
    ) {
        return {};
    }

    if (
        error.message.startsWith('BotProt:Error') &&
        error.message.includes('429')
    ) {
        return {
            message:
                'You have exceeded the maximum number of attempts for the security check. To keep ticket sales fair and secure, this session cannot be verified.',
            title: 'Verification limit reached!',
            cta: 'Try again!',
        };
    }

    return {};
}

function getLocationFromResponse(response: Response): string {
    if (!('headers' in response) || !response.headers) {
        throw new Error(
            `BotProt init call failed. Invalid response: ${response?.status} ${response?.statusText}`
        );
    }

    const location = response.headers.get('location');
    if (!location) {
        throw new Error(`BotProt init call failed. Invalid location header.`);
    }

    return location;
}
